/**
 * MONSTER TOWN — MONSTER ANIMATION. MONSTER CREW (src/monster/**).
 *
 * Code-rigged, procedural, deterministic. No clips, no skinning, no morph
 * targets — an older iPad gets ~20 float lerps and a handful of transform
 * writes per monster per frame, and that is the whole cost.
 *
 * HOW IT WORKS
 *  1. A BASE pose is computed every frame from locomotion speed + an idle clock
 *     (breathing, head drift, blinking, the occasional fidget).
 *  2. An ACTION (sit, sleep, eat, cheer, wave, carry, dance…) copies that pose
 *     and overrides the parts it cares about.
 *  3. The two are blended by `k`, which eases in and out, so nothing ever snaps.
 *
 * ADDING AN ACTION IS DATA-SHAPED: one entry in `ACTIONS`. Nothing else changes.
 * An unknown action id still animates (it falls back to a nod) — a prop that
 * asks for something silly must never leave the monster standing dead still,
 * because LAW #1 says every tap does something a kid can SEE.
 */


import { orientFace, type MonsterRig } from './art'
import {
  ACTIONS,
  TAU,
  blankPose,
  blendPose,
  copyPose,
  resolveAction,
  type Pose,
} from './actions'

export { actionIds, resolveAction } from './actions'

// ---------------------------------------------------------------------------
// animator
// ---------------------------------------------------------------------------

const BLEND_IN = 0.14
const BLEND_OUT = 0.22
/**
 * HOW LONG AN ACTION IS SAFE FROM THE "WALKED OFF" GUARD.
 *
 * `walkedOff` drops a pose the moment the monster is moving, so a kid who taps
 * the floor mid-cuddle gets a monster that stands up and walks. Correct — but
 * it was tested against `this.speed`, which is the SMOOTHED locomotion blend,
 * and that is still high on the frame the walker arrives: the walker brakes at
 * 18 u/s and the blend eases with its own time constant, so for roughly six
 * frames after arriving the animator still believes the monster is walking.
 *
 * Every prop reaction in the game is delivered on exactly that frame —
 * `finishArrival` emits `play:interacted`, the interaction runner turns it into
 * `monster:action`, and the animator killed it before `actionK` had left zero.
 * The monster reached the thing the kid tapped and then did nothing at all,
 * silently, and only after a walk long enough to reach full speed. Short hops
 * (the telly across a small room) survived, which is why it read as flaky
 * rather than broken and why the bus trace looked perfectly healthy.
 *
 * A quarter of a second of grace covers the brake ramp from full speed with
 * room to spare. A kid who genuinely walks away is unaffected: `play:walkTo`
 * calls `stop()` on the animator directly, on the frame of the tap.
 */
const WALK_OFF_GRACE = 0.25
const FIDGETS = ['lookAround', 'wiggle', 'hop', 'shrug', 'think'] as const

export class MonsterAnimator {
  private readonly rig: MonsterRig
  private readonly rng: () => number

  private t = 0
  private phase = 0
  private speed = 0
  private speedTarget = 0

  private blinkIn: number
  private blinkFor = 0
  private fidgetIn: number

  private actionName: string | null = null
  private actionT = 0
  private actionDur = 0
  private actionK = 0
  private releasing = false

  private readonly base = blankPose()
  private readonly over = blankPose()
  private readonly out = blankPose()

  constructor(rig: MonsterRig, rng: () => number = Math.random) {
    this.rig = rig
    this.rng = rng
    this.blinkIn = 1.5 + rng() * 3
    this.fidgetIn = 4 + rng() * 6
  }

  /** 0 = standing still, 1 = full walk. Anything between blends. */
  setSpeed(v: number): void {
    this.speedTarget = Math.max(0, Math.min(1, v))
  }

  get currentAction(): string | null {
    return this.actionName
  }

  get busy(): boolean {
    return this.actionName !== null && !this.releasing
  }

  /** `seconds <= 0` holds the pose until something else replaces it. */
  play(action: string, seconds?: number): void {
    const name = resolveAction(action)
    const def = ACTIONS[name]
    if (def === undefined) return
    this.actionName = name
    this.actionT = 0
    this.actionDur = seconds !== undefined && seconds > 0 ? seconds : def.seconds
    this.releasing = false
    this.fidgetIn = 5 + this.rng() * 6
  }

  /** Ease out of whatever is playing. */
  stop(): void {
    if (this.actionName !== null) this.releasing = true
  }

  update(dt: number): void {
    const d = Math.min(0.1, Math.max(0, dt))
    this.t += d

    // -- locomotion ------------------------------------------------------
    const k = 1 - Math.pow(0.0008, d)
    this.speed += (this.speedTarget - this.speed) * k
    this.phase += d * (6.6 + this.speed * 2.2) * Math.max(0.0001, this.speed)
    if (this.phase > TAU * 1024) this.phase -= TAU * 1024

    // -- action clock ----------------------------------------------------
    let def = this.actionName === null ? undefined : ACTIONS[this.actionName]
    if (def !== undefined) {
      this.actionT += d
      const walkedOff =
        this.actionT > WALK_OFF_GRACE && this.speed > 0.25 && def.whileWalking !== true
      if (walkedOff) this.releasing = true
      if (this.actionDur > 0 && this.actionT >= this.actionDur) this.releasing = true
      if (this.releasing) {
        this.actionK -= d / BLEND_OUT
        if (this.actionK <= 0) {
          this.actionK = 0
          this.actionName = null
          this.releasing = false
          def = undefined
        }
      } else {
        this.actionK = Math.min(1, this.actionK + d / BLEND_IN)
      }
    } else {
      this.actionK = 0
    }

    // -- blink -----------------------------------------------------------
    if (this.blinkFor > 0) {
      this.blinkFor -= d
    } else {
      this.blinkIn -= d
      if (this.blinkIn <= 0) {
        this.blinkFor = 0.11
        this.blinkIn = 1.8 + this.rng() * 3.6
      }
    }

    // -- idle fidget -----------------------------------------------------
    if (this.actionName === null && this.speed < 0.05) {
      this.fidgetIn -= d
      if (this.fidgetIn <= 0) {
        const pick = FIDGETS[Math.floor(this.rng() * FIDGETS.length)] ?? 'lookAround'
        this.play(pick)
      }
    }

    this.buildBase()
    if (def !== undefined && this.actionK > 0) {
      copyPose(this.base, this.over)
      const u = this.actionDur > 0 ? Math.min(1, this.actionT / this.actionDur) : (this.actionT * 0.5) % 1
      def.pose(this.over, u, this.t)
      blendPose(this.base, this.over, this.actionK, this.out)
    } else {
      copyPose(this.base, this.out)
    }
    this.apply(this.out)
  }

  private buildBase(): void {
    const p = this.base
    const w = this.speed
    const i = 1 - w
    const s1 = Math.sin(this.phase)
    const s2 = Math.sin(this.phase * 2)
    const breathe = Math.sin(this.t * 2.1)
    const drift = Math.sin(this.t * 0.53)

    p.bounceY = Math.abs(s1) * 0.1 * w
    p.crouch = 0
    p.bounceRotX = 0.11 * w
    p.bounceRotY = 0
    p.bounceRotZ = -s1 * 0.05 * w
    p.bodyScaleY = 1 - s2 * 0.055 * w + breathe * 0.03 * i
    p.bodyRotX = 0
    p.bodyRotZ = s1 * 0.03 * w
    p.headRotX = -0.05 * w + Math.sin(this.t * 0.87) * 0.05 * i
    p.headRotY = drift * 0.22 * i
    p.headRotZ = s1 * 0.07 * w
    p.armLX = -s1 * 0.8 * w + i * (0.05 + breathe * 0.07)
    p.armRX = s1 * 0.8 * w + i * (0.05 - breathe * 0.07)
    p.armLZ = 0.2 + i * breathe * 0.06
    p.armRZ = -0.2 - i * breathe * 0.06
    p.legLX = s1 * 0.72 * w
    p.legRX = -s1 * 0.72 * w
    p.lid = this.blinkFor > 0 ? 0.06 : 1
    p.mouth = 1
  }

  private apply(p: Pose): void {
    const r = this.rig
    r.bounce.position.y = p.bounceY - p.crouch * r.hipDrop
    r.bounce.rotation.set(p.bounceRotX, p.bounceRotY, p.bounceRotZ)

    const sy = Math.max(0.4, p.bodyScaleY)
    const sxz = 1 + (1 - sy) * 0.6
    r.body.scale.set(sxz, sy, sxz)
    r.body.rotation.set(p.bodyRotX, 0, p.bodyRotZ)

    r.head.rotation.set(p.headRotX, p.headRotY, p.headRotZ)
    r.armL.rotation.set(p.armLX, 0, p.armLZ)
    r.armR.rotation.set(p.armRX, 0, p.armRZ)
    r.legL.rotation.x = p.legLX
    r.legR.rotation.x = p.legRX

    const lid = Math.max(0.04, p.lid) * r.eyeOpen
    for (const e of r.eyes) e.scale.y = lid

    if (r.mouth !== null) r.mouth.scale.y = Math.max(0.2, p.mouth)

    if (r.antennae !== null) {
      const s1 = Math.sin(this.phase)
      r.antennae.rotation.z = -s1 * 0.28 * this.speed + Math.sin(this.t * 3.1) * 0.07
      r.antennae.rotation.x = -Math.sin(this.phase + 1.1) * 0.22 * this.speed
    }

    /**
     * THE FACE TURNS LAST, because it turns against everything above it.
     *
     * Doing it here rather than in each crew's render loop is the point: the
     * player, the neighbours and the venue's customers all drive their monsters
     * through this animator, so all three got the fix at once and a fourth crew
     * spawning a monster tomorrow gets it for free. `head.rotation.y` has just
     * been written above, so the idle head-drift is already accounted for.
     */
    orientFace(r)
  }
}
